import { useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Unlink, ExternalLink } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { detectWallets, connectWallet, signText, openInPhantom } from "@/lib/solana";
import { SolMark } from "@/components/ClaimNftDialog";

export default function SolanaLink({ game, refresh }) {
  const [busy, setBusy] = useState(false);
  const w = game.sol_wallet;
  const wallets = detectWallets();
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(e?.response ? errMsg(e) : e.message || "Something went wrong"); } setBusy(false); };
  const link = (id) => run(async () => {
    const { provider, address } = await connectWallet(id);
    const { data: ch } = await api.post("/sol/challenge", { wallet: address, purpose: "link" });
    const signature = await signText(provider, ch.message);
    const { data } = await api.post("/sol/link", { wallet: address, nonce: ch.nonce, signature });
    await refresh();
    toast.success(data.pct ? `Linked! ${data.sol_wallet.count} 1/1 wolf NFT(s) found: +${data.pct}% to every stat` : "Linked. No 1/1 Wolf NFTs found in this wallet yet.");
  });
  const recheck = () => run(async () => { await api.post("/sol/refresh"); await refresh(); toast.success("1/1 Wolves re-checked"); });
  const unlink = () => run(async () => { await api.post("/sol/unlink"); await refresh(); toast("Solana wallet unlinked. The 1/1 Wolves boost is off."); });
  return (
    <section className="glass rounded-2xl p-6 mb-8 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="sol-wallet-status">
      <span className="w-8 h-8 rounded-full bg-gradient-to-br from-[#9945FF] to-[#14F195] flex items-center justify-center shrink-0"><SolMark className="w-4 h-4 text-white" /></span>
      {w ? (
        <>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-mono-game text-slate-400">LINKED SOLANA WALLET · 1/1 WOLVES</p>
            <p data-testid="sol-wallet-address" className="font-mono-game text-emerald-200 truncate">{w.address}</p>
            <p className="text-[11px] text-slate-500 mt-1" data-testid="sol-wallet-count">Holding {w.count} 1/1 Wolf NFT{w.count === 1 ? "" : "s"} · +{game.boosts.sol_pct}% to every stat{w.checked_at ? ` · checked ${new Date(w.checked_at).toLocaleString()}` : ""}</p>
          </div>
          <button data-testid="sol-refresh-btn" disabled={busy} onClick={recheck} className="flex items-center gap-2 px-4 py-2 rounded-full border border-slate-600 text-sm hover:border-emerald-400"><RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />Re-check</button>
          <button data-testid="sol-unlink-btn" disabled={busy} onClick={unlink} className="flex items-center gap-2 px-4 py-2 rounded-full text-sm text-slate-400 hover:text-red-300"><Unlink className="w-4 h-4" />Unlink</button>
        </>
      ) : (
        <>
          <p className="flex-1 text-slate-300">Hold 1/1 Wolf NFTs on Solana? Link that wallet for +5% to every stat per wolf, up to +25%.</p>
          {wallets.length ? wallets.map((x) => (
            <button key={x.id} data-testid={`sol-link-${x.id}`} disabled={busy} onClick={() => link(x.id)} className="ember-btn px-5 py-3 rounded-full text-sm disabled:opacity-40">{busy ? "WAITING FOR WALLET…" : `LINK ${x.name.toUpperCase()}`}</button>
          )) : (
            <a data-testid="sol-get-wallet" href={openInPhantom()} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-5 py-3 rounded-full border border-emerald-400/50 text-emerald-200 text-sm hover:bg-emerald-500/10">Open in Phantom <ExternalLink className="w-4 h-4" /></a>
          )}
        </>
      )}
    </section>
  );
}
